import type { NormalizedSeries } from "./seriesData";
import type { MediaTimelineBarLabels } from "../MediaTimelineBar/timelineTypes";

export type SeriesReadoutLabels = Pick<
  Required<MediaTimelineBarLabels>,
  | "series"
  | "seriesValues"
  | "seriesTime"
  | "seriesValue"
  | "seriesAxisRange"
  | "seriesScrollHint"
  | "seriesSample"
  | "seriesInterpolated"
  | "seriesHeld"
  | "seriesNoData"
>;

export const DEFAULT_SERIES_READOUT_LABELS: SeriesReadoutLabels = {
  series: "Series",
  seriesValues: "Series values",
  seriesTime: "Time (x)",
  seriesValue: "Value (y)",
  seriesAxisRange: "Axis range",
  seriesScrollHint: "Scroll with the wheel over the row, or Tab to it and use Page Up / Page Down.",
  seriesSample: "Sample",
  seriesInterpolated: "Interpolated",
  seriesHeld: "Held",
  seriesNoData: "No data",
};

export type SeriesReading =
  | { value: number; kind: "sample" | "linear" | "step" }
  | { value: null; kind: "gap" };

/**
 * Raw-data lookup, independent of the canvas envelope and the authored y-axis clamp. Past the
 * last sample the value is held for `holdSeconds`, and reads as held there rather than as a
 * sample or an interpolation.
 */
export function readSeriesAt(
  { times, values, interpolation, holdSeconds }: NormalizedSeries,
  time: number,
): SeriesReading {
  const gap = { value: null, kind: "gap" } as const;
  if (!Number.isFinite(time) || !times.length || time < times[0]!) return gap;
  const lastTime = times[times.length - 1]!;
  if (time > lastTime) {
    if (time > lastTime + holdSeconds) return gap;
    const held = values[values.length - 1]!;
    return Number.isFinite(held) ? { value: held, kind: "step" } : gap;
  }
  // Upper bound: the last authored sample at a duplicate timestamp owns the value after its jump.
  let low = 0;
  let high = times.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (times[middle]! <= time) low = middle + 1;
    else high = middle;
  }
  const left = low - 1;
  const value = values[left]!;
  if (!Number.isFinite(value)) return gap;
  if (times[left] === time) return { value, kind: "sample" };
  if (interpolation === "step") return { value, kind: "step" };
  const rightValue = values[low]!;
  if (!Number.isFinite(rightValue)) return gap;
  const fraction = (time - times[left]!) / (times[low]! - times[left]!);
  // Weighted endpoints also avoid overflowing the difference between large opposite values.
  return { value: (1 - fraction) * value + fraction * rightValue, kind: "linear" };
}

/** Keep small signals visible, while bounding the width of the comparison's numeric column. */
export function formatSeriesValue(value: number): string {
  return Number(value.toPrecision(6)).toString();
}
